import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { ExternalImage } from '../Components/ExternalContent';
import FlashAlert from '../Components/FlashAlert/FlashAlert';
import apiService from '../services/api.service';
import { appPath } from '../utils/appSlug';
import { resolveAssetUrl } from '../utils/assets';
import { formatRomeDateTime, parseApiDate } from '../utils/datetime';
import { getDashboardHomePath, getRole, ROLES } from '../utils/roles';
import { tenantSessionAllowed } from '../utils/tenant';
import './myEvents.css';

const MyEvents = ({ appSlug, loginStatus }) => {
  const history = useHistory();
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const ordinaryUser = getRole(loginStatus) === ROLES.UTENTE_BASE;
  const canLoad = Boolean(loginStatus?.sub && ordinaryUser && loginStatus.is_verified !== false && appSlug &&
    tenantSessionAllowed(loginStatus, { slug: appSlug }));

  useEffect(() => {
    if (!canLoad) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    setEvents([]);
    apiService.getMyEvents().then(({ data }) => {
      if (active) setEvents(data || []);
    }).catch(() => {
      if (active) setError('Impossibile caricare i tuoi eventi. Ricarica la pagina per riprovare.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [canLoad, appSlug, loginStatus?.sub]);

  if (loginStatus?.sub && !ordinaryUser) return <Redirect to={getDashboardHomePath(loginStatus)} />;
  if (!canLoad) return <Redirect to={appPath(appSlug, 'login')} />;

  const participate = (event) => {
    if (event.finished) return;
    sessionStorage.setItem(`course-participate-${event.id}`, 'true');
    history.push(appPath(appSlug, `course/${event.id}`));
  };

  return (
    <div className="my-events-page">
      <div className="my-events-content">
        <header className="my-events-header">
          <h1>I miei eventi</h1>
          <p>Qui trovi gli eventi a cui sei iscritto.</p>
        </header>
        {location.state?.successReason && <FlashAlert variant="success">{location.state.successReason}</FlashAlert>}
        {loading && <p role="status">Caricamento eventi...</p>}
        {error && <Alert variant="danger" role="alert">{error}</Alert>}
        {!loading && !error && events.length === 0 && <Alert variant="info">
          Non sei ancora iscritto a nessun evento. Per iscriverti, usa il link di iscrizione fornito dall’organizzatore.
        </Alert>}
        {!loading && !error && events.length > 0 && <div className="my-events-grid">
          {events.map((event) => {
            const date = parseApiDate(event.date);
            const upcoming = date && date > new Date();
            return <article className="my-event-card" key={event.id} aria-labelledby={`my-event-${event.id}`}>
              <div className="my-event-cover">
                {event.cover_url ? <ExternalImage src={resolveAssetUrl(event.cover_url)} alt={event.name}
                  className="my-event-cover__image" loading="lazy" /> :
                  <div className="my-event-cover__placeholder">Copertina evento</div>}
              </div>
              <div className="my-event-body">
                <span className={`my-event-status${event.finished ? ' my-event-status--finished' : ''}`}>
                  {event.finished ? 'Terminato' : upcoming ? 'In programma' : 'Disponibile'}
                </span>
                <h2 id={`my-event-${event.id}`}>{event.name}</h2>
                {event.date && <p className="my-event-date"><time dateTime={date ? date.toISOString() : undefined}>
                  {formatRomeDateTime(event.date)}
                </time></p>}
                {event.description && <p className="my-event-description">{event.description}</p>}
                {!event.finished && <div className="my-event-actions">
                  <Button onClick={() => participate(event)}>Partecipa</Button>
                </div>}
              </div>
            </article>;
          })}
        </div>}
      </div>
    </div>
  );
};

export default MyEvents;
